// ============================================================
// VIDEO CALL CLIENT — plain browser WebRTC.
//
// Message protocol matches src/videoCall/signaling/webrtc.signaling.js
// EXACTLY — this is not a new protocol, it's the same one
// webrtcCall.service.ts (RN) already speaks:
//
//   → { type: 'auth', taskId, idToken, callType, applicationId }
//   ← { type: 'auth-ok', roomName, identity, peerPresent }
//   ← { type: 'peer-joined', from }
//   → { type: 'offer', sdp }         (also relayed with `from` added)
//   ← { type: 'offer', sdp, from }
//   → { type: 'answer', sdp }
//   ← { type: 'answer', sdp, from }
//   → { type: 'ice-candidate', candidate }
//   ← { type: 'ice-candidate', candidate, from }
//   ← { type: 'peer-left', from }
//   → { type: 'screen-share-start' }  / 'screen-share-stop'
//   ← same, relayed with `from`
//   → { type: 'leave' }  (best-effort, sent right before ws.close())
//
// No react-native-webrtc here — this runs in an actual browser
// (or the WebView's embedded browser engine), so the NATIVE
// browser RTCPeerConnection / getUserMedia / getDisplayMedia APIs
// are used directly. This is exactly the class of native-module
// crash (Skia surfaces, camera hardware release timing, gesture
// handler teardown races) that doesn't exist here — the browser
// engine owns and manages all of this itself, the same reliable
// way it does for every WebRTC site on the internet.
// ============================================================

export function createVideoCallClient({ apiBaseUrl, wsPath, iceServers, taskId, callType, applicationId, sessionId, idToken, voiceOnly, onState }) {
  let ws = null
  let pc = null
  let localStream = null
  let localScreenStream = null
  let screenSender = null
  let pendingCandidates = []
  let initialNegotiationDone = false
  let expectingScreenTrack = false
  let tornDown = false

  // state pushed to the caller (React) via onState — mirrors the
  // shape webrtcCall.service.ts's hook returns.
  const state = {
    connectionState: 'connecting', // connecting | connected | disconnected | failed
    localStream: null,
    remoteStream: null,
    localScreenStream: null,
    remoteScreenStream: null,
    micEnabled: true,
    cameraEnabled: !voiceOnly,
    isSharingScreen: false,
    // Set when the call ends because the server force-closed it
    // (currently: the time limit was reached — see 'call-ended'
    // below). null for every other end-of-call path (peer hung up,
    // connection failed, local leave). CallView/App.jsx can use
    // this to show a specific message instead of a generic one.
    endReason: null,
  }
  const push = (patch) => { Object.assign(state, patch); onState({ ...state }) }

  const wsUrl = `${apiBaseUrl.replace(/^http/, 'ws')}${wsPath}`

  const sendMessage = (payload) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)) } catch { /* ignore */ }
    }
  }

  const flushPendingCandidates = async (peerConn) => {
    const candidates = pendingCandidates.splice(0)
    for (const c of candidates) {
      try { await peerConn.addIceCandidate(c) } catch { /* ignore */ }
    }
  }

  const createPC = (stream) => {
    if (pc) {
      try { pc.close() } catch { /* ignore */ }
      console.log("PC CLOSED", sessionId)
      pc = null
    }
    initialNegotiationDone = false
    pendingCandidates = []

    const peerConn = new RTCPeerConnection({ iceServers })
    pc = peerConn
    console.log("PC CREATED", sessionId)

    stream.getTracks().forEach((track) => peerConn.addTrack(track, stream))

    peerConn.ontrack = (event) => {
      if (tornDown || !event.streams?.[0]) return
      if (expectingScreenTrack) {
        expectingScreenTrack = false
        push({ remoteScreenStream: event.streams[0] })
      } else {
        push({ remoteStream: event.streams[0] })
      }
    }

    peerConn.onicecandidate = (event) => {
      if (tornDown) return
      if (event.candidate) {
        console.log("ICE CANDIDATE", sessionId, event.candidate)
        sendMessage({ type: 'ice-candidate', candidate: event.candidate })
      }
    }

    peerConn.onconnectionstatechange = () => {
      if (tornDown) return
      const s = peerConn.connectionState
      console.log("PC STATE", sessionId, s)
      if (s === 'connected') {
        initialNegotiationDone = true
        push({ connectionState: 'connected' })
      } else if (s === 'disconnected') {
        push({ connectionState: 'disconnected' })
      } else if (s === 'failed') {
        push({ connectionState: 'failed' })
      }
    }

    peerConn.onsignalingstatechange = () => {
      console.log("SIGNALING STATE", sessionId, peerConn.signalingState)
    }

    peerConn.oniceconnectionstatechange = () => {
      console.log("ICE STATE", sessionId, peerConn.iceConnectionState)
    }

    peerConn.onnegotiationneeded = async () => {
      if (!initialNegotiationDone || tornDown) return
      try {
        const offer = await peerConn.createOffer()
        if (tornDown) return
        await peerConn.setLocalDescription(offer)
        if (tornDown) return
        sendMessage({ type: 'offer', sdp: peerConn.localDescription.sdp })
      } catch (err) {
        console.error('Renegotiation offer failed:', err)
      }
    }

    return peerConn
  }

  const start = async () => {
    console.log("CALL CREATED", sessionId)
    try {
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: voiceOnly ? false : { facingMode: 'user' },
      })
    } catch (err) {
      console.error('getUserMedia failed:', err)
      if (!tornDown) push({ connectionState: 'failed' })
      return
    }
    if (tornDown) {
      localStream.getTracks().forEach((t) => t.stop())
      return
    }
    push({ localStream })

    ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      if (tornDown) return
      sendMessage({ type: 'auth', taskId, idToken, callType, applicationId: applicationId || undefined, sessionId: sessionId || undefined })
    }

    ws.onmessage = async (event) => {
      if (tornDown) return
      let msg
      try { msg = JSON.parse(event.data) } catch { return }

      switch (msg.type) {
        case 'auth-ok': {
          // We joined the room. Set up the PC so incoming tracks and
          // ICE candidates have somewhere to land.
          // If peerPresent=true we are the SECOND joiner (callee) —
          // we do NOT offer. The first joiner (caller) will receive
          // 'peer-joined' from the server and send the offer; we just
          // wait to answer it.
          // If peerPresent=false we are the FIRST joiner (caller) —
          // we also do nothing yet; we wait for 'peer-joined' which
          // the server sends us when the callee connects.
          if (!localStream) return
          createPC(localStream)
          return
        }
        case 'peer-joined': {
          // The server sends this ONLY to the first joiner (caller)
          // when the second joiner (callee) connects. We are the
          // initiator — create and send the offer now.
          // The callee's 'auth-ok' handler already set up their PC
          // and is waiting to answer.
          //
          // Previously this handler only called createPC() without
          // sending an offer, while 'auth-ok' with peerPresent sent
          // the offer from the second joiner's side. That was
          // backwards: the callee was offering to a caller whose PC
          // was just created by this 'peer-joined' handler and had no
          // remote description set — so setRemoteDescription on an
          // unexpected offer type threw and the call never connected.
          if (!pc || tornDown) return
          try {
            const offer = await pc.createOffer()
            if (tornDown) return
            await pc.setLocalDescription(offer)
            if (tornDown) return
            sendMessage({ type: 'offer', sdp: pc.localDescription.sdp })
          } catch (err) {
            console.error('Failed to create offer on peer-joined:', err)
            if (!tornDown) push({ connectionState: 'failed' })
          }
          return
        }
        case 'offer': {
          if (!pc || tornDown) return
          console.log("OFFER RECEIVED", sessionId)
          try {
            await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp })
            await flushPendingCandidates(pc)
            const answer = await pc.createAnswer()
            if (tornDown) return
            await pc.setLocalDescription(answer)
            sendMessage({ type: 'answer', sdp: pc.localDescription.sdp })
          } catch (err) {
            console.error('Failed to process incoming offer:', err)
            if (!tornDown) push({ connectionState: 'failed' })
          }
          return
        }
        case 'answer': {
          if (!pc || tornDown) return
          console.log("ANSWER RECEIVED", sessionId)
          await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp })
          await flushPendingCandidates(pc)
          return
        }
        case 'ice-candidate': {
          if (!msg.candidate) return
          console.log("ICE CANDIDATE RECEIVED", sessionId, msg.candidate)
          const candidate = new RTCIceCandidate(msg.candidate)
          if (pc?.remoteDescription) {
            try { await pc.addIceCandidate(candidate) } catch { /* ignore */ }
          } else {
            pendingCandidates.push(candidate)
          }
          return
        }
        case 'peer-left':
          // Terminal — the other side left. Update state only.
          // Do NOT call handleLeave or attempt reconnect — the UI
          // (App.jsx's connectionState effect) handles navigation.
          push({ connectionState: 'disconnected', remoteStream: null, remoteScreenStream: null })
          return
        case 'call-ended':
          // Terminal — the SERVER ended this call (currently only
          // fired when the time limit is reached; see
          // videoCall.service.js's endOverdueWebrtcCalls and
          // webrtc.signaling.js's forceEndRoom). Same handling
          // shape as peer-left: update state only, let App.jsx's
          // connectionState effect drive navigation. endReason
          // lets the UI show a specific message ("Call time limit
          // reached") instead of the generic hang-up message.
          //
          // The server closes this socket right after sending this
          // message (see webrtc.signaling.js), so ws.onclose will
          // also fire immediately after — it already no-ops once
          // connectionState is 'disconnected', so no double-push.
          console.log('CALL ENDED BY SERVER', sessionId, msg.reason)
          push({ connectionState: 'disconnected', remoteStream: null, remoteScreenStream: null, endReason: msg.reason || 'Call ended' })
          return
           case 'screen-share-start':
          expectingScreenTrack = true
          return
        case 'screen-share-stop':
          push({ remoteScreenStream: null })
          return
        case 'whiteboard-open':
          push({ peerOpenedWhiteboard: true })
          return
      }
    }

    ws.onerror = () => { if (!tornDown) push({ connectionState: 'failed' }) }
    ws.onclose = () => {
      // If tornDown, leave() already ran — do nothing.
      if (tornDown) return
      // If peer-left already pushed 'disconnected', the App.jsx
      // useEffect will have called handleLeave and set leftRef=true.
      // A second push here would fire the useEffect again (same state
      // value, so React bails — but only on the next render, not
      // synchronously). Safest: skip the push entirely when we're
      // already in a terminal disconnected/failed state, since there's
      // nothing new to communicate and the UI is already reacting.
      if (state.connectionState === 'disconnected' || state.connectionState === 'failed') return
      push({ connectionState: 'disconnected' })
    }
  }

  const toggleMic = () => {
    const next = !state.micEnabled
    localStream?.getAudioTracks().forEach((t) => { t.enabled = next })
    push({ micEnabled: next })
  }

  const toggleCamera = () => {
    const next = !state.cameraEnabled
    localStream?.getVideoTracks().forEach((t) => { t.enabled = next })
    push({ cameraEnabled: next })
  }

  const stopScreenShare = () => {
    if (pc && screenSender) {
      try { pc.removeTrack(screenSender) } catch { /* ignore */ }
      screenSender = null
    }
    localScreenStream?.getTracks().forEach((t) => t.stop())
    localScreenStream = null
    push({ localScreenStream: null, isSharingScreen: false })
    sendMessage({ type: 'screen-share-stop' })
  }

  const startScreenShare = async () => {
    if (!pc) return
    try {
      // Browser-native getDisplayMedia — the browser itself excludes
      // its own chrome from the capture and, in a WebView context,
      // this call surfaces the OS's normal screen-picker UI. No
      // MediaProjectionService, no manifest permission, none of the
      // Android foreground-service plumbing the native module needed.
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
      localScreenStream = screenStream
      push({ localScreenStream: screenStream, isSharingScreen: true })

      const track = screenStream.getVideoTracks()[0]
      screenSender = pc.addTrack(track, screenStream)
      expectingScreenTrack = true
      sendMessage({ type: 'screen-share-start' })
      track.onended = () => stopScreenShare()
    } catch (err) {
      // User cancelled the picker, or permission denied — not fatal.
      console.warn('Screen share cancelled or failed:', err)
    }
  }

    const notifyWhiteboardOpen = () => sendMessage({ type: 'whiteboard-open' })

  const leave = () => {
    if (tornDown) return
    tornDown = true
    console.log("CALL ENDED", sessionId)
    sendMessage({ type: 'leave' })
    if (ws) { ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null; try { ws.close() } catch { /* ignore */ } }
    ws = null
    if (pc) {
      pc.ontrack = pc.onicecandidate = pc.onconnectionstatechange = pc.onsignalingstatechange = pc.oniceconnectionstatechange = pc.onnegotiationneeded = null
      try { pc.close() } catch { /* ignore */ }
      console.log("PC CLOSED", sessionId)
    }
    pc = null
    localStream?.getTracks().forEach((t) => t.stop())
    localStream = null
    localScreenStream?.getTracks().forEach((t) => t.stop())
    localScreenStream = null
    push({
      connectionState: 'disconnected', localStream: null, remoteStream: null,
      localScreenStream: null, remoteScreenStream: null, isSharingScreen: false,
    })
  }

  start()

    return { toggleMic, toggleCamera, startScreenShare, stopScreenShare, leave, notifyWhiteboardOpen }
}